import React, { forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Plot from 'react-plotly.js';

import dayjs from 'dayjs';
import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';

import './Report.css';

function createData(time: any, temp: any) {
    return { time, temp };
}

const Graph = (props: any) => {
    return (
        <div className='graph'>
            <Plot
                data={[
                    {
                        x: [1, 2, 3, 4, 5, 6, 7],
                        y: [28, 28, 27, 27, 28, 28, 28],
                        type: 'scatter',
                        mode: 'lines',
                        marker: { color: '#e6e6ff' },
                        showlegend: false,
                    },
                    {
                        x: [],
                        y: [],
                        type: 'scatter',
                        mode: 'markers',
                        marker: { color: '#3F8EE8' },
                        name: '正常',
                    },
                    // {
                    //     x: [3, 8, 10, 11, 12],
                    //     y: [28, 28, 28, 28, 28],
                    //     type: 'scatter',
                    //     mode: 'markers',
                    //     marker: {color: '#FF3838'},
                    //     name: '異常',
                    // },
                    // {type: 'bar', x: [1, 2, 3], y: [2, 5, 3]},
                ]}
                layout={{
                    width: 525,
                    height: 350,
                    title: '趨勢變化圖',
                    paper_bgcolor: '#FFFFFF',
                    plot_bgcolor: '#FFFFFF',
                }}
                divId='graph'
                config={{ staticPlot: true }}
            />
        </div>
    );
};

const Report = forwardRef<HTMLDivElement, any>((props: any, ref: any) => {
    const { reportData = {} } = props;
    const { t } = useTranslation(undefined, { keyPrefix: 'Report' });

    const tempUnit = reportData.tempUnit === 'fahrenheit' ? 'fahrenheit' : 'celsius';
    const tempSymbol = tempUnit === 'fahrenheit' ? '°F' : '°C';

    const tempData = Array.isArray(reportData.detailRowData)
        ? reportData.detailRowData.map((row: any) => {
              return createData(dayjs(row.time).format('YYYY-MM-DD HH:mm'), row.temperature);
          })
        : [];

    const lineColor = reportData.is_normal ? '#4485C6' : '#E64644';

    const duration = tempData.length * 10;
    const durationD = Math.floor(duration / 60 / 24);
    const durationH = Math.floor((duration % (60 * 24)) / 60);
    const durationM = duration % 60;
    const durationAboveThreshold = tempData.filter((row: any) => row.temp > reportData.upperLimit).length * 10;
    const durationAboveThresholdD = Math.floor(durationAboveThreshold / 60 / 24);
    const durationAboveThresholdH = Math.floor((durationAboveThreshold % (60 * 24)) / 60);
    const durationAboveThresholdM = durationAboveThreshold % 60;
    const durationBelowThreshold = tempData.filter((row: any) => row.temp < reportData.lowerLimit).length * 10;
    const durationBelowThresholdD = Math.floor(durationBelowThreshold / 60 / 24);
    const durationBelowThresholdH = Math.floor((durationBelowThreshold % (60 * 24)) / 60);
    const durationBelowThresholdM = durationBelowThreshold % 60;
    const durationOutOfThreshold = reportData.timeLimit / 60;
    const durationOutOfThresholdD = Math.floor(durationOutOfThreshold / 60 / 24);
    const durationOutOfThresholdH = Math.floor((durationOutOfThreshold % (60 * 24)) / 60);
    const durationOutOfThresholdM = durationOutOfThreshold % 60;
    const intervalD = Math.floor(reportData.interval / 60 / 60 / 24);
    const intervalH = Math.floor(reportData.interval / 60 / 60) % 24;
    const intervalM = Math.floor(reportData.interval / 60) % 60;
    const maxTemp = Math.max(...tempData.map((row: any) => row.temp));
    const minTemp = Math.min(...tempData.map((row: any) => row.temp));
    // const rowCount = reportData.detailRowData ? reportData.detailRowData.length/4 : 0;
    // const tempDataDivided = tempData.reduce((acc, e, i) => (i % 4 ? acc[acc.length - 1].push(e) : acc.push([e]), acc), []);
    // fill tempData to multiple of 200
    const tempDataFilled = tempData.concat(Array(200 - (tempData.length % 200)).fill({ time: '', temp: '' }));
    // divide by page and 200 records per page (50 records per column)
    const tempDataDivided = tempDataFilled.reduce(
        (acc: any, e: any, i: any) => (i % 200 ? acc[acc.length - 1].push(e) : acc.push([e]), acc),
        []
    );
    const tempDataRow: any[] = [];
    tempDataDivided.forEach((page: any, outIndex: any) => {
        tempDataRow.push([]);
        for (let i = 0; i < 50; i++) {
            tempDataRow[outIndex].push([]);
            tempDataRow[outIndex][i].push(page[i]);
            tempDataRow[outIndex][i].push(page[i + 50]);
            tempDataRow[outIndex][i].push(page[i + 100]);
            tempDataRow[outIndex][i].push(page[i + 150]);
        }
    });
    // const tempDataDivided = tempDataFilled.reduce((acc, e, i) => (i % 50 ? acc[acc.length - 1].push(e) : acc.push([e]), acc), []);
    // const tempTranspose = tempDataDivided[0].map((col, i) => tempDataDivided.map(row => row[i]));
    // console.log(tempDataRow);

    return (
        <div ref={ref} className='report' style={{ width: '570pt', margin: '10pt' }}>
            <div style={{ display: 'inline', float: 'left' }}>
                {t('fileCreated')}: {dayjs().format('YYYY-MM-DD HH:mm')}
            </div>
            <div style={{ display: 'inline', float: 'right' }}>
                {t('status')}:{' '}
                {reportData.is_normal ? <font color='blue'>{t('pass')}</font> : <font color='red'>{t('fail')}</font>}
            </div>
            <hr style={{ width: '100%' }}></hr>
            <div
                style={{
                    width: '100%',
                    height: '60pt',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: 'auto',
                    backgroundColor: '#080074',
                }}
            >
                <p style={{ color: '#FFFFFF', fontSize: '25pt' }}>{t('dataReport')}</p>
            </div>
            <table
                style={{ width: '100%', border: '1px black solid', borderCollapse: 'collapse', margin: '10px auto' }}
            >
                <tbody>
                    <tr>
                        <td>Delivered by:</td>
                        <td>{reportData.shipper}</td>
                    </tr>
                    <tr>
                        <td>{t('periodCovered')}:</td>
                        <td>
                            {reportData.startTime} to {reportData.endTime}
                        </td>
                    </tr>
                    <tr style={{ borderTop: '1px black solid' }}>
                        <td>{t('timeZone')}</td>
                        <td>{reportData.timeZone}</td>
                    </tr>
                </tbody>
            </table>

            <hr></hr>
            <table style={{ width: '100%', margin: 'auto' }}>
                <tbody>
                    <tr style={{ backgroundColor: '#5271FF', margin: 0 }}>
                        <th colSpan={4} style={{ height: '20pt', color: '#FFFFFF' }}>
                            {t('deviceInformation')}
                        </th>
                    </tr>
                    <tr>
                        <td>{t('deviceModel')}</td>
                        <td>{reportData.model_info?.model}</td>
                        <td>{t('sensorType')}</td>
                        <td>{t('temperature')}</td>
                    </tr>
                    <tr>
                        <td>{t('deviceId')}</td>
                        <td>{reportData.id}</td>
                        <td>{t('loggingInterval')}</td>
                        <td>
                            {intervalD}
                            {t('d')} {intervalH}
                            {t('h')} {intervalM}
                            {t('m')}
                        </td>
                    </tr>
                </tbody>
            </table>
            <hr></hr>
            <table style={{ width: '100%', margin: 'auto' }}>
                <tbody>
                    <tr style={{ backgroundColor: '#5271FF' }}>
                        <th colSpan={4} style={{ height: '20pt', color: '#FFFFFF' }}>
                            {t('dataDetails')}
                        </th>
                    </tr>
                    <tr>
                        <td>{t('numberOfRecords')}</td>
                        <td>{reportData.number_of_record}</td>
                        <td>{t('maximumTemperature')}</td>
                        <td>
                            {maxTemp}
                            {tempSymbol}
                        </td>
                    </tr>
                    <tr>
                        <td>{t('durationOfRecords')}</td>
                        <td>
                            {durationD}
                            {t('d')} {durationH}
                            {t('h')} {durationM}
                            {t('m')}
                        </td>
                        <td>{t('minimumTemperature')}</td>
                        <td>
                            {minTemp}
                            {tempSymbol}
                        </td>
                    </tr>
                    <tr>
                        <td rowSpan={2}>{t('status')}</td>
                        <td rowSpan={2}>
                            {reportData.is_normal ? (
                                <font color='blue'>{t('pass')}</font>
                            ) : (
                                <font color='red'>{t('fail')}</font>
                            )}
                        </td>
                        <td>
                            {t('durationOver')} {reportData.upperLimit}
                            {tempSymbol}
                        </td>
                        <td>
                            {durationAboveThresholdD}
                            {t('d')} {durationAboveThresholdH}
                            {t('h')} {durationAboveThresholdM}
                            {t('m')}
                        </td>
                    </tr>
                    <tr>
                        <td>
                            {t('durationBelow')} {reportData.lowerLimit}
                            {tempSymbol}
                        </td>
                        <td>
                            {durationBelowThresholdD}
                            {t('d')} {durationBelowThresholdH}
                            {t('h')} {durationBelowThresholdM}
                            {t('m')}
                        </td>
                    </tr>
                    <tr>
                        <td>{t('excursionAlarms')}</td>
                        <td colSpan={3}>
                            {reportData.thresholdType === 'continuous' ? t('singleEvent') : t('cumulative')}:{' '}
                            {t('outOf')} {reportData.upperLimit}
                            {tempSymbol} {t('to')} {reportData.lowerLimit}
                            {tempSymbol} {t('for')} {durationOutOfThresholdD}
                            {t('d')} {durationOutOfThresholdH}
                            {t('h')} {durationOutOfThresholdM}
                            {t('m')}
                        </td>
                    </tr>
                </tbody>
            </table>
            <hr></hr>
            <table style={{ width: '100%', margin: 'auto' }}>
                <tbody>
                    <tr style={{ backgroundColor: '#5271FF' }}>
                        <th colSpan={1} style={{ height: '20pt', color: '#FFFFFF' }}>
                            {t('temperatureGraph')}
                        </th>
                    </tr>
                    <tr>
                        <td>
                            <Plot
                                data={[
                                    {
                                        x: tempData.map((row: any) => row.time),
                                        y: tempData.map((row: any) => row.temp),
                                        type: 'scatter',
                                        mode: 'lines',
                                        marker: { color: '#000000' },
                                        showlegend: false,
                                    },
                                    {
                                        x: tempData.map((row: any) => row.time),
                                        y: tempData.map(() => reportData.upperLimit),
                                        type: 'scatter',
                                        mode: 'lines',
                                        marker: { color: '#FF3838' },
                                        line: { dash: 'dash' },
                                        showlegend: false,
                                    },
                                    {
                                        x: tempData.map((row: any) => row.time),
                                        y: tempData.map(() => reportData.lowerLimit),
                                        type: 'scatter',
                                        mode: 'lines',
                                        marker: { color: '#3F8EE8' },
                                        line: { dash: 'dash' },
                                        showlegend: false,
                                    },
                                    // {
                                    //     x: [3, 8, 10, 11, 12],
                                    //     y: [28, 28, 28, 28, 28],
                                    //     type: 'scatter',
                                    //     mode: 'markers',
                                    //     marker: {color: '#FF3838'},
                                    //     name: '異常',
                                    // },
                                    // {type: 'bar', x: [1, 2, 3], y: [2, 5, 3]},
                                ]}
                                layout={{
                                    xaxis: { tickangle: 0 },
                                    yaxis: {
                                        range: [
                                            minTemp < reportData.lowerLimit ? minTemp - 5 : reportData.lowerLimit - 5,
                                            maxTemp > reportData.upperLimit ? maxTemp + 5 : reportData.upperLimit + 5,
                                        ],
                                        zeroline: false,
                                    },
                                    width: 755,
                                    height: 645,
                                    margin: { b: 100 },
                                    paper_bgcolor: '#FFFFFF',
                                    plot_bgcolor: '#FFFFFF',
                                }}
                                divId='graph'
                                config={{ staticPlot: true }}
                            />
                        </td>
                    </tr>
                </tbody>
            </table>

            {tempDataRow.map((page: any, pgindex: any) => {
                return (
                    <div key={pgindex}>
                        <hr style={{ marginTop: '30px' }}></hr>
                        <table key={`table-${pgindex}`} style={{ width: '100%', margin: '10px auto 60px auto' }}>
                            <tbody>
                                <tr key={`page-${pgindex}`} style={{ backgroundColor: '#5271FF' }}>
                                    <th colSpan={12} style={{ height: '20pt', color: '#FFFFFF' }}>
                                        {t('dataRecords')}
                                    </th>
                                </tr>
                                <tr className='subheader' key={`'header'-${pgindex}`}>
                                    <th>{t('date')}</th>
                                    <th>{t('time')}</th>
                                    <th>{tempSymbol}</th>
                                    <th>{t('date')}</th>
                                    <th>{t('time')}</th>
                                    <th>{tempSymbol}</th>
                                    <th>{t('date')}</th>
                                    <th>{t('time')}</th>
                                    <th>{tempSymbol}</th>
                                    <th>{t('date')}</th>
                                    <th>{t('time')}</th>
                                    <th>{tempSymbol}</th>
                                </tr>
                                {page.map((row: any, rindex: any) => {
                                    return (
                                        <tr key={`record-${pgindex}-${rindex}`}>
                                            {row.map((col: any, cindex: any) => {
                                                let color = '#000000';
                                                if (col.temp > reportData.upperLimit) {
                                                    color = '#FF3838';
                                                } else if (col.temp < reportData.lowerLimit) {
                                                    color = '#3F8EE8';
                                                }
                                                return (
                                                    <React.Fragment key={`column-${pgindex}-${rindex}-${cindex}`}>
                                                        <td
                                                            key={`column-${pgindex}-${rindex}-${cindex}-1`}
                                                            style={{ color: color }}
                                                        >
                                                            {col.time.split(' ')[0]}
                                                        </td>
                                                        <td
                                                            key={`column-${pgindex}-${rindex}-${cindex}-2`}
                                                            style={{ color: color }}
                                                        >
                                                            {col.time.split(' ')[1]}
                                                        </td>
                                                        <td
                                                            key={`column-${pgindex}-${rindex}-${cindex}-3`}
                                                            style={{ color: color }}
                                                        >
                                                            {col.temp}
                                                        </td>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                );
            })}
            {/* <tr style={{backgroundColor: '#5271FF'}}>
                    <th colSpan={12} style={{height: '20pt', color: '#FFFFFF'}}>Data Records</th>
                </tr>
                <tr className="subheader">
                    <th>Date</th>
                    <th>Time</th>
                    <th>{tempSymbol}</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>{tempSymbol}</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>{tempSymbol}</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>{tempSymbol}</th>
                </tr>
                {
                    tempDataDivided[0].map((row, index) => {
                        return (
                            <tr key={index}>
                                <td>{row.time.split(' ')[0]}</td>
                                <td>{row.time.split(' ')[1]}</td>
                                <td>{row.temp}</td>
                                <td>{tempDataDivided[1][index] ? tempDataDivided[1][index].time.split(' ')[0] : ''}</td>
                                <td>{tempDataDivided[1][index] ? tempDataDivided[1][index].time.split(' ')[1] : ''}</td>
                                <td>{tempDataDivided[1][index] ? tempDataDivided[1][index].temp : ''}</td>
                                <td>{tempDataDivided[2][index] ? tempDataDivided[2][index].time.split(' ')[0] : ''}</td>
                                <td>{tempDataDivided[2][index] ? tempDataDivided[2][index].time.split(' ')[1] : ''}</td>
                                <td>{tempDataDivided[2][index] ? tempDataDivided[2][index].temp : ''}</td>
                                <td>{tempDataDivided[3][index] ? tempDataDivided[3][index].time.split(' ')[0] : ''}</td>
                                <td>{tempDataDivided[3][index] ? tempDataDivided[3][index].time.split(' ')[1] : ''}</td>
                                <td>{tempDataDivided[3][index] ? tempDataDivided[3][index].temp : ''}</td>
                            </tr>
                        )
                    })

                } */}
        </div>
    );
});

export default Report;
